import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BrandMark from "@/components/BrandMark";
import DemoPersonaLogin from "@/components/DemoPersonaLogin";
import SupportContact from "@/components/SupportContact";
import { SpinnerGap, ShieldCheck, Key, Eye, EyeSlash } from "@phosphor-icons/react";

const inputCls =
  "mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent transition-shadow";
const labelCls = "text-xs uppercase tracking-[0.2em] font-semibold text-slate-500";
const buttonCls =
  "w-full flex items-center justify-center gap-2 rounded-md bg-slate-900 text-white py-2.5 text-sm font-medium hover:bg-slate-800 active:scale-[0.99] transition-all duration-200 disabled:opacity-60";

/**
 * Authentication is staged (FR-24): the password never yields a session on its
 * own. The server's staged response selects the next factor, password change,
 * recovery-code acknowledgement, or completed session.
 */
export default function Login() {
  const [stage, setStage] = useState("password"); // password | challenge | enroll | codes | password-change | password-changed
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState("");
  const [interimToken, setInterimToken] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [passwordChangeToken, setPasswordChangeToken] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [demoRole, setDemoRole] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  const interimHeaders = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

  const fail = (err) =>
    toast.error(formatApiError(err.response?.data?.detail) || err.message);

  const clearInterimAuthentication = () => {
    setPassword("");
    setCode("");
    setInterimToken(null);
    setEnrollment(null);
    setRecoveryCodes([]);
    setPasswordChangeToken(null);
    setNewPassword("");
    setConfirmPassword("");
  };

  const restartAfterExpiredToken = (err) => {
    const rawDetail = err.response?.data?.detail;
    const detail = typeof rawDetail === "string" ? rawDetail : formatApiError(rawDetail);
    if (!["Token expired", "Invalid token", "Authentication token is stale"].includes(detail)) {
      return false;
    }
    clearInterimAuthentication();
    setStage("password");
    toast.error("Your authentication step expired. Sign in again.");
    return true;
  };

  const continueAfterAuthentication = async (data) => {
    if (data.password_change_required) {
      setPasswordChangeToken(data.change_token);
      setStage("password-change");
      return;
    }
    await login();
    navigate("/");
  };

  const authenticatePassword = async (loginEmail, loginPassword) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", {
        email: loginEmail,
        password: loginPassword,
      });
      if (data.authenticated || data.access_token || data.password_change_required) {
        await continueAfterAuthentication(data);
        return;
      }
      if (data.mfa_required) {
        setInterimToken(data.challenge_token);
        setStage("challenge");
        return;
      }
      if (data.mfa_enrollment_required) {
        const token = data.enrollment_token;
        setInterimToken(token);
        const { data: enrol } = await api.post("/auth/mfa/enroll", {},
          interimHeaders(token));
        setEnrollment(enrol);
        setStage("enroll");
      }
    } catch (err) {
      fail(err);
    } finally {
      setLoading(false);
    }
  };

  const submitPassword = (e) => {
    e.preventDefault();
    return authenticatePassword(email, password);
  };

  const loginAsDemo = async (persona, demoPassword) => {
    setEmail(persona.email);
    setPassword(demoPassword);
    setDemoRole(persona.role);
    try {
      await authenticatePassword(persona.email, demoPassword);
    } finally {
      setDemoRole("");
    }
  };

  const submitChallenge = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/mfa/challenge", { code },
        interimHeaders(interimToken));
      if (data.mfa_method === "recovery") {
        toast.warning(
          `Recovery code used. ${data.recovery_codes_remaining ?? 0} remaining.`
        );
      }
      await continueAfterAuthentication(data);
    } catch (err) {
      setCode("");
      if (!restartAfterExpiredToken(err)) fail(err);
    } finally {
      setLoading(false);
    }
  };

  const submitEnrollment = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/mfa/enroll/verify", { code },
        interimHeaders(interimToken));
      if (data.password_change_required) {
        setPasswordChangeToken(data.change_token);
      }
      setRecoveryCodes(data.recovery_codes || []);
      setStage("codes");
    } catch (err) {
      setCode("");
      if (!restartAfterExpiredToken(err)) fail(err);
    } finally {
      setLoading(false);
    }
  };

  const finishEnrollment = async () => {
    if (passwordChangeToken) {
      setStage("password-change");
      return;
    }
    await login();
    navigate("/");
  };

  const submitExpiredPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await api.post(
        "/auth/password/expired",
        { current_password: password, new_password: newPassword },
        interimHeaders(passwordChangeToken),
      );
      setPassword("");
      setNewPassword("");
      setConfirmPassword("");
      try {
        await login();
        navigate("/");
      } catch {
        clearInterimAuthentication();
        setStage("password-changed");
        toast.error(
          "Your password was changed, but the session could not be loaded. Sign in again.",
        );
      }
    } catch (err) {
      if (!restartAfterExpiredToken(err)) fail(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2.5 mb-10">
            <div className="w-9 h-9 rounded-md bg-slate-900 flex items-center justify-center">
              <BrandMark size={22} className="text-white" />
            </div>
            <span className="font-display font-semibold text-xl text-slate-900">BioLIMS</span>
          </div>

          {stage === "password" && (
            <>
              <h1 className="text-3xl font-display font-semibold tracking-tight text-slate-900">Sign in</h1>
              <p className="text-slate-500 mt-2 mb-8 text-sm">
                Laboratory Information Management System
              </p>
              <form onSubmit={submitPassword} className="space-y-4">
                <div>
                  <label className={labelCls} htmlFor="login-email">Email</label>
                  <input data-testid="login-email" id="login-email" type="email" value={email}
                    onChange={(e) => setEmail(e.target.value)} required className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="login-password">Password</label>
                  <div className="relative">
                    <input data-testid="login-password" id="login-password" type={showPassword ? "text" : "password"} value={password}
                      onChange={(e) => setPassword(e.target.value)} required className={`${inputCls} pr-11`} />
                    <button
                      type="button"
                      data-testid="login-password-visibility"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1.5 text-slate-400 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-900"
                      onPointerDown={(e) => {
                        e.preventDefault();
                        setShowPassword(true);
                      }}
                      onPointerUp={() => setShowPassword(false)}
                      onPointerLeave={() => setShowPassword(false)}
                      onPointerCancel={() => setShowPassword(false)}
                      onKeyDown={(e) => {
                        if ((e.key === "Enter" || e.key === " ") && !e.repeat) {
                          e.preventDefault();
                          setShowPassword(true);
                        }
                      }}
                      onKeyUp={(e) => {
                        if (e.key === "Enter" || e.key === " ") setShowPassword(false);
                      }}
                      onBlur={() => setShowPassword(false)}
                      onContextMenu={(e) => e.preventDefault()}
                    >
                      {showPassword ? <EyeSlash size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
                    </button>
                  </div>
                </div>
                <button data-testid="login-submit" disabled={loading} className={buttonCls}>
                  {loading && <SpinnerGap size={18} className="animate-spin" />}
                  Continue
                </button>
              </form>
              <p className="mt-6 text-xs text-slate-400">
                Accounts are created by your laboratory administrator.
              </p>
              <DemoPersonaLogin
                onSelect={loginAsDemo}
                disabled={loading}
                activeRole={demoRole}
              />
            </>
          )}

          {stage === "challenge" && (
            <>
              <div className="flex items-center gap-2 text-slate-900">
                <ShieldCheck size={22} weight="duotone" />
                <h1 className="text-2xl font-display font-semibold tracking-tight">
                  Two-factor authentication
                </h1>
              </div>
              <p className="text-slate-500 mt-2 mb-8 text-sm">
                Enter the six-digit code from your authenticator app, or one of your
                recovery codes.
              </p>
              <form onSubmit={submitChallenge} className="space-y-4">
                <div>
                  <label className={labelCls} htmlFor="mfa-code">Code</label>
                  <input data-testid="mfa-code" id="mfa-code" value={code} autoFocus autoComplete="one-time-code"
                    onChange={(e) => setCode(e.target.value)} required
                    className={`${inputCls} font-mono tracking-[0.3em]`} placeholder="000000" />
                </div>
                <button data-testid="mfa-submit" disabled={loading} className={buttonCls}>
                  {loading && <SpinnerGap size={18} className="animate-spin" />}
                  Verify
                </button>
              </form>
            </>
          )}

          {stage === "enroll" && (
            <>
              <div className="flex items-center gap-2 text-slate-900">
                <ShieldCheck size={22} weight="duotone" />
                <h1 className="text-2xl font-display font-semibold tracking-tight">
                  Set up two-factor authentication
                </h1>
              </div>
              <p className="text-slate-500 mt-2 mb-5 text-sm">
                Two-factor authentication is required for every account. Scan this code
                with an authenticator app, then enter the six digits it shows.
              </p>
              {enrollment?.qr_svg && (
                <div
                  data-testid="mfa-qr"
                  className="w-44 h-44 mx-auto mb-4 [&>svg]:w-full [&>svg]:h-full"
                  dangerouslySetInnerHTML={{ __html: enrollment.qr_svg }}
                />
              )}
              <details className="mb-5">
                <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-900">
                  Can't scan? Enter the key manually
                </summary>
                <code className="mt-2 block break-all rounded-md bg-slate-50 border border-slate-200 p-2 text-xs font-mono text-slate-700">
                  {enrollment?.secret}
                </code>
              </details>
              <form onSubmit={submitEnrollment} className="space-y-4">
                <div>
                  <label className={labelCls} htmlFor="mfa-enroll-code">Verification code</label>
                  <input data-testid="mfa-enroll-code" id="mfa-enroll-code" value={code} autoComplete="one-time-code"
                    onChange={(e) => setCode(e.target.value)} required
                    className={`${inputCls} font-mono tracking-[0.3em]`} placeholder="000000" />
                </div>
                <button data-testid="mfa-enroll-submit" disabled={loading} className={buttonCls}>
                  {loading && <SpinnerGap size={18} className="animate-spin" />}
                  Activate
                </button>
              </form>
            </>
          )}

          {stage === "codes" && (
            <>
              <div className="flex items-center gap-2 text-slate-900">
                <Key size={22} weight="duotone" />
                <h1 className="text-2xl font-display font-semibold tracking-tight">
                  Save your recovery codes
                </h1>
              </div>
              <p className="text-slate-500 mt-2 mb-5 text-sm">
                Each code signs you in once if you lose your authenticator. They are
                shown now and never again. Store them somewhere safe.
              </p>
              <div data-testid="recovery-codes"
                className="grid grid-cols-2 gap-1.5 rounded-md border border-slate-200 bg-slate-50 p-3 font-mono text-xs text-slate-700">
                {recoveryCodes.map((c) => <span key={c}>{c}</span>)}
              </div>
              <button
                onClick={() => {
                  navigator.clipboard?.writeText(recoveryCodes.join("\n"));
                  toast.success("Copied to clipboard");
                }}
                className="mt-3 text-xs text-slate-500 hover:text-slate-900"
              >
                Copy all
              </button>
              <button data-testid="recovery-continue" onClick={finishEnrollment}
                className={`${buttonCls} mt-5`}>
                I have saved them, continue
              </button>
            </>
          )}

          {stage === "password-change" && (
            <>
              <div className="flex items-center gap-2 text-slate-900">
                <Key size={22} weight="duotone" />
                <h1 className="text-2xl font-display font-semibold tracking-tight">
                  Choose a new password
                </h1>
              </div>
              <p className="text-slate-500 mt-2 mb-8 text-sm">
                Your temporary or expired password must be replaced before you can
                continue. Use at least 12 characters with upper- and lowercase
                letters, a number, and a symbol.
              </p>
              <form onSubmit={submitExpiredPassword} className="space-y-4">
                <div>
                  <label className={labelCls} htmlFor="new-password">New password</label>
                  <input
                    data-testid="new-password"
                    id="new-password"
                    type="password"
                    value={newPassword}
                    autoComplete="new-password"
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={12}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls} htmlFor="confirm-password">Confirm new password</label>
                  <input
                    data-testid="confirm-password"
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    autoComplete="new-password"
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={12}
                    className={inputCls}
                  />
                </div>
                <button data-testid="password-change-submit" disabled={loading} className={buttonCls}>
                  {loading && <SpinnerGap size={18} className="animate-spin" />}
                  Change password and continue
                </button>
              </form>
            </>
          )}

          {stage === "password-changed" && (
            <>
              <div className="flex items-center gap-2 text-slate-900">
                <ShieldCheck size={22} weight="duotone" />
                <h1 className="text-2xl font-display font-semibold tracking-tight">
                  Password changed
                </h1>
              </div>
              <p className="text-slate-500 mt-2 mb-8 text-sm">
                Your new password is saved. The session could not be loaded, so
                sign in again with the new password.
              </p>
              <button
                data-testid="password-changed-sign-in"
                onClick={() => setStage("password")}
                className={buttonCls}
              >
                Sign in again
              </button>
            </>
          )}
          <SupportContact />
        </div>
      </div>

      <div className="hidden lg:block relative overflow-hidden bg-slate-950">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_20%,rgba(45,212,191,0.28),transparent_34%),radial-gradient(circle_at_78%_68%,rgba(56,189,248,0.2),transparent_38%),linear-gradient(145deg,#020617,#0f172a_55%,#134e4a)]" />
        <div className="absolute -right-24 top-20 h-96 w-96 rounded-full border border-white/10" />
        <div className="absolute -right-8 top-36 h-72 w-72 rounded-full border border-white/10" />
        <div className="absolute right-16 top-52 h-48 w-48 rounded-full border border-white/10" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2 className="text-3xl font-display font-semibold leading-tight">
            Track every specimen, from collection to result.
          </h2>
          <p className="mt-3 text-white/80 max-w-md">
            Sample tracking, storage mapping, assay results and turnaround-time
            reporting for your laboratory.
          </p>
        </div>
      </div>
    </div>
  );
}
